import { loadDb, saveDb } from '../db';
import { mockNow } from '../clock';
import { addNotification, makeReference } from '../phase4';
import { CC_NETWORK_CHOICE_ENABLED } from '@/api/flags';
import { mockId } from '../util';
import type { CardApplication, CreditCard } from '@/types/phase4.types';

const CATALOGUE = [
  { id: 'NOVA-CLASSIC', name: 'Nova Classic', network: 'VISA', feeRupees: 0, limitRupees: 100000 },
  { id: 'NOVA-RUPAY', name: 'Nova RuPay', network: 'RUPAY', feeRupees: 499, limitRupees: 150000 },
  { id: 'NOVA-TRAVEL', name: 'Nova Travel', network: 'MASTERCARD', feeRupees: 999, limitRupees: 250000 },
];

export const catalogue = () => CATALOGUE;
export const networkChoiceEnabled = (): boolean => {
  const db = loadDb();
  return CC_NETWORK_CHOICE_ENABLED || db.phase4.networkChoiceEnabled;
};
export const applications = (): Promise<CardApplication[]> => Promise.resolve(loadDb().phase4.cardApplications);
export const cards = (): Promise<CreditCard[]> => Promise.resolve(loadDb().phase4.cards);

const makeCard = (db: ReturnType<typeof loadDb>, product: typeof CATALOGUE[number], network: string): CreditCard => {
  const now = mockNow(db);
  return {
    id: mockId('card'), cardId: product.id, network, status: 'NOT_ACTIVATED', approvedAt: now,
    activateByAt: now + 30 * 86400000, reminderSent: false, limitRupees: product.limitRupees,
    availableRupees: product.limitRupees, outstandingRupees: 0, rewardPoints: 0, billingDay: 5,
    cycleChanged: false, locked: false,
    channels: { atm: true, pos: true, online: true, contactless: true, international: false, internationalOnline: false, internationalAtm: false },
    transactions: [], statements: [], emis: [], upiLinked: false,
    number: `4917${Math.floor(100000000000 + Math.random() * 899999999999)}`,
    expiry: '12/31', cvv: '123',
  };
};

export const apply = (input: { cardId: string; network?: string; addOn?: CardApplication['addOn'] }): Promise<CardApplication> => {
  const db = loadDb();
  const product = CATALOGUE.find((c) => c.id === input.cardId);
  if (!product) return Promise.reject(new Error('Select a valid credit card.'));
  const network = input.network && (CC_NETWORK_CHOICE_ENABLED || db.phase4.networkChoiceEnabled) ? input.network : product.network;
  const application: CardApplication = { id: mockId('ccapp'), reference: makeReference('CC'), cardId: product.id, network, stage: 'APPROVED', addOn: input.addOn, submittedAt: mockNow(db) };
  db.phase4.cardApplications.unshift(application);
  db.phase4.cards.unshift(makeCard(db, product, network));
  addNotification(db, 'Credit card approved', `Application ${application.reference} is approved. Your virtual card is ready.`);
  saveDb(db);
  return Promise.resolve(application);
};

export const activate = (id: string): Promise<CreditCard> => {
  const db = loadDb(); const card = db.phase4.cards.find((c) => c.id === id);
  if (!card) return Promise.reject(new Error('Card not found.'));
  card.status = 'ACTIVE'; card.locked = false; saveDb(db); return Promise.resolve(card);
};

export const setChannel = (id: string, channel: keyof CreditCard['channels'], enabled: boolean): Promise<void> => {
  const db = loadDb(); const card = db.phase4.cards.find((c) => c.id === id);
  if (!card) return Promise.reject(new Error('Card not found.'));
  card.channels[channel] = enabled; saveDb(db); return Promise.resolve();
};
export const setLocked = (id: string, locked: boolean): Promise<void> => { const db = loadDb(); const card = db.phase4.cards.find((c) => c.id === id); if (!card) return Promise.reject(new Error('Card not found.')); card.locked = locked; saveDb(db); return Promise.resolve(); };
export const linkUpi = (id: string): Promise<void> => { const db = loadDb(); const card = db.phase4.cards.find((c) => c.id === id); if (!card) return Promise.reject(new Error('Card not found.')); if (card.network !== 'RUPAY') return Promise.reject(new Error('Only RuPay cards can be linked to UPI.')); card.upiLinked = true; saveDb(db); return Promise.resolve(); };
export const changeNetwork = (id: string, network: string): Promise<void> => { const db = loadDb(); if (!CC_NETWORK_CHOICE_ENABLED && !db.phase4.networkChoiceEnabled) return Promise.reject(new Error('Network choice is currently unavailable.')); const card = db.phase4.cards.find((c) => c.id === id); if (!card) return Promise.reject(new Error('Card not found.')); card.network = network; saveDb(db); return Promise.resolve(); };
export const close = (id: string): Promise<void> => { const db = loadDb(); const card = db.phase4.cards.find((c) => c.id === id); if (!card) return Promise.reject(new Error('Card not found.')); card.status = 'CLOSED'; card.closure = { requestedAt: mockNow(db), completeByAt: mockNow(db) + 7 * 86400000, compensatedDays: 0 }; addNotification(db, 'Credit card closure requested', `Card ${card.number.slice(-4)} closure is in progress.`); saveDb(db); return Promise.resolve(); };
export const payBill = (id: string, amountRupees: number): Promise<void> => {
  const db = loadDb();
  const card = db.phase4.cards.find((c) => c.id === id);
  if (!card) return Promise.reject(new Error('Card not found.'));
  if (amountRupees <= 0 || amountRupees > card.outstandingRupees) return Promise.reject(new Error('Enter a valid bill amount.'));
  card.outstandingRupees -= amountRupees;
  card.availableRupees = Math.min(card.limitRupees, card.availableRupees + amountRupees);
  addNotification(db, 'Credit card bill paid', `₹${amountRupees.toLocaleString('en-IN')} payment received.`);
  saveDb(db);
  return Promise.resolve();
};
export const changeBillingDay = (id: string, day: number): Promise<void> => {
  const db = loadDb();
  const card = db.phase4.cards.find((c) => c.id === id);
  if (!card) return Promise.reject(new Error('Card not found.'));
  if (!Number.isInteger(day) || day < 1 || day > 28) return Promise.reject(new Error('Choose a billing day from 1 to 28.'));
  card.billingDay = day;
  card.cycleChanged = true;
  saveDb(db);
  return Promise.resolve();
};
export const convertToEmi = (id: string, txnId: string, months: number): Promise<void> => {
  const db = loadDb();
  const card = db.phase4.cards.find((c) => c.id === id);
  const txn = card?.transactions.find((item) => item.id === txnId);
  if (!card || !txn) return Promise.reject(new Error('Card transaction not found.'));
  if (![3, 6, 9, 12, 18, 24].includes(months)) return Promise.reject(new Error('Choose a supported EMI tenure.'));
  card.emis.push({ id: mockId('emi'), txnId, months, monthlyRupees: Math.ceil(txn.amountRupees / months), remaining: months });
  saveDb(db);
  return Promise.resolve();
};
export const resetPin = (id: string): Promise<void> => {
  const db = loadDb();
  const card = db.phase4.cards.find((c) => c.id === id);
  if (!card) return Promise.reject(new Error('Card not found.'));
  addNotification(db, 'Credit card PIN reset', `PIN reset requested for card ending ${card.number.slice(-4)}.`);
  saveDb(db);
  return Promise.resolve();
};
